import { ChatRequestError, toChatError, type ChatError } from './errors';
import type { MintedToken } from './http';
import { parseEnvelope } from './protocol/envelope';
import type { WireEnvelope } from './protocol/types';

export type ConnectionStatus = 'connecting' | 'open' | 'recovering' | 'offline';
export interface PageSignals {
  isVisible(): boolean;
  onVisible(cb: () => void): () => void;
  onOnline(cb: () => void): () => void;
}
export interface ConnectionOptions {
  mint: () => Promise<MintedToken>;
  sdkVersion: string;
  onFrame: (env: WireEnvelope) => void;
  onStatus: (status: ConnectionStatus) => void;
  onOpen: () => void;
  onReconnected: () => void;
  onError: (error: ChatError) => void;
  onClosed: (reason: string) => void;
  WebSocketImpl?: typeof WebSocket;
  page?: PageSignals;
  random?: () => number;
}

export const PING_INTERVAL_MS = 25_000;
export const SILENCE_LIMIT_MS = 60_000;
export const ROTATE_LEAD_MS = 30_000;
export const AGENT_INACTIVE_RETRY_MS = 60_000;
export const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const ROTATE_RETRY_MS = 10_000;
const CLIENT_ROTATE = 4000;
const MAX_AUTH_CLOSES = 2;
const TERMINAL_MINT_CODES = new Set(['invalid_token', 'session_not_found', 'session_closed', 'origin_not_allowed', 'agent_archived', 'agent_not_found', 'public_chat_disabled']);

export function browserPage(): PageSignals {
  return {
    isVisible: () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
    onVisible: (cb) => {
      const h = () => { if (document.visibilityState !== 'hidden') cb(); };
      if (typeof document === 'undefined') return () => {};
      document.addEventListener('visibilitychange', h);
      return () => document.removeEventListener('visibilitychange', h);
    },
    onOnline: (cb) => {
      if (typeof window === 'undefined') return () => {};
      window.addEventListener('online', cb);
      return () => window.removeEventListener('online', cb);
    },
  };
}

function detach(s: WebSocket | null): void {
  if (!s) return;
  s.onopen = null; s.onmessage = null; s.onclose = null; s.onerror = null;
}

export class StreamConnection {
  private socket: WebSocket | null = null;   // current socket
  private next: WebSocket | null = null;     // rotation socket, not yet open
  private stopped = false;
  private started = false;
  private everOpened = false;
  private failures = 0;
  private authCloses = 0;                    // consecutive 4401 closes without a successful open
  private epoch = 0;                         // bumped on every close of the current socket
  private rotating = false;
  private pingSupported = true;
  private lastInbound = 0;
  private lastPingSentAt = 0;
  private expiresAt = 0;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private waitingForPage = new Set<() => void>();
  private readonly page: PageSignals;
  private readonly WS: typeof WebSocket;
  private readonly random: () => number;

  constructor(private readonly opts: ConnectionOptions) {
    this.page = opts.page ?? browserPage();
    this.WS = opts.WebSocketImpl ?? WebSocket;
    this.random = opts.random ?? Math.random;
  }

  get isOpen(): boolean {
    return !this.stopped && this.socket?.readyState === 1;
  }

  start(): void {
    if (this.started || this.stopped) return;
    this.started = true;
    this.opts.onStatus('connecting');
    void this.connect();
  }

  send(frame: Record<string, unknown>): boolean {
    if (!this.isOpen) return false;
    this.socket!.send(JSON.stringify(frame));
    return true;
  }

  stop(): void {
    this.stopped = true;
    this.epoch += 1;
    this.clearTimers();
    this.stopPings();
    this.waitingForPage.forEach((off) => off());
    this.waitingForPage.clear();
    this.abandon(this.next); this.next = null;
    this.abandon(this.socket); this.socket = null;
  }

  private abandon(s: WebSocket | null): void {
    detach(s);
    try { s?.close(1000, 'client-stop'); } catch { /* already closed */ }
  }

  private recoveringStatus(): ConnectionStatus {
    return this.everOpened ? 'recovering' : 'connecting';
  }

  private urlFor(m: MintedToken): string {
    const sep = m.wsUrl.includes('?') ? '&' : '?';
    return `${m.wsUrl}${sep}token=${encodeURIComponent(m.token)}&sdk=${encodeURIComponent(this.opts.sdkVersion)}`;
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    const epoch = this.epoch;
    let minted: MintedToken;
    try {
      minted = await this.opts.mint();
    } catch (e) {
      if (this.stopped || epoch !== this.epoch) return;
      this.handleMintFailure(e);
      return;
    }
    if (this.stopped || epoch !== this.epoch) return;
    try { this.openSocket(minted, false); } catch (e) { this.handleMintFailure(e); }
  }

  // Handlers are bound to `s`: events from a socket that is no longer current are ignored.
  private openSocket(minted: MintedToken, rotation: boolean): void {
    const s = new this.WS(this.urlFor(minted));
    if (rotation) this.next = s; else { this.socket = s; this.expiresAt = minted.expiresAt; }
    s.onopen = () => {
      if (this.stopped) return;
      if (rotation) { if (this.next === s) this.swapIn(s, minted.expiresAt); } else if (this.socket === s) this.handleOpen();
    };
    s.onmessage = (e: MessageEvent) => { if (this.socket === s) this.handleMessage(e.data); };
    s.onclose = (e: CloseEvent) => {
      if (this.stopped) return;
      if (this.next === s) {   // rotation socket died before opening: keep the current one, retry later
        detach(s);
        this.next = null;
        this.later(ROTATE_RETRY_MS, () => this.whenVisible(() => void this.rotate()));
        return;
      }
      if (this.socket === s) this.handleClose(e.code, e.reason);
    };
  }

  private handleOpen(): void {
    const reconnect = this.everOpened;
    this.everOpened = true;
    this.failures = 0;
    this.authCloses = 0;
    this.pingSupported = true;
    this.lastInbound = Date.now();
    this.opts.onStatus('open');
    if (this.stopped) return;
    this.startPings();
    this.scheduleRotation();
    if (this.stopped) return;
    this.opts.onOpen();
    if (reconnect && !this.stopped) this.opts.onReconnected();
  }

  private swapIn(s: WebSocket, expiresAt: number): void {
    const old = this.socket;
    this.next = null;
    this.socket = s;
    this.expiresAt = expiresAt;
    this.failures = 0;
    this.authCloses = 0;
    this.pingSupported = true;
    detach(old);
    try { old?.close(CLIENT_ROTATE, 'token-refresh'); } catch { /* already closed */ }
    this.clearTimers();
    this.lastInbound = Date.now();
    this.startPings();
    this.scheduleRotation();
    this.opts.onOpen();          // client resends unacked frames
    if (!this.stopped) this.opts.onReconnected();   // client reconciles; status stays 'open'
  }

  private handleMessage(data: unknown): void {
    if (this.stopped) return;
    this.lastInbound = Date.now();
    const env = parseEnvelope(data);
    if (!env) return;
    if (env.type === 'ws_write_result' && env.payload.error === 'invalid_input_type' && !env.payload.message_id) {
      this.pingSupported = false;   // engine predates ping; rely on reconnects
      this.stopPings();
      return;
    }
    this.opts.onFrame(env);
  }

  private handleClose(code: number, reason: string): void {
    if (this.stopped) return;
    this.epoch += 1;
    detach(this.socket);
    this.socket = null;
    this.abandon(this.next); this.next = null;
    this.rotating = false;
    this.stopPings();
    this.clearTimers();
    this.clearPageListeners();
    if (code === 4401) {
      this.authCloses += 1;
      if (this.authCloses > MAX_AUTH_CLOSES) {   // reconnecting is not fixing it
        this.opts.onError(toChatError('invalid_token'));
        this.scheduleBackoff();
        return;
      }
      this.opts.onStatus(this.recoveringStatus());
      this.whenVisible(() => this.reconnectNow());
      return;
    }
    if (code === 1008 && reason === 'agent_inactive') {
      this.opts.onError(toChatError('agent_inactive'));
      if (!this.stopped) this.opts.onStatus(this.recoveringStatus());
      this.later(AGENT_INACTIVE_RETRY_MS, () => this.reconnectNow());
      return;
    }
    if (code === 1008) { this.terminate(reason || 'policy'); return; }
    this.scheduleBackoff();
  }

  private handleMintFailure(e: unknown): void {
    const err = e instanceof ChatRequestError ? e.error : toChatError('network');
    if (err.code === 'agent_inactive') {
      this.opts.onError(err);
      if (!this.stopped) this.opts.onStatus(this.recoveringStatus());
      this.later(AGENT_INACTIVE_RETRY_MS, () => this.reconnectNow());
      return;
    }
    if (TERMINAL_MINT_CODES.has(err.code)) { this.terminate(err.code); return; }
    if (err.code === 'rate_limited' && err.retryAfterSeconds) {
      this.opts.onError(err);
      if (!this.stopped) this.opts.onStatus(this.recoveringStatus());
      this.later(err.retryAfterSeconds * 1000, () => this.reconnectNow());
      return;
    }
    this.scheduleBackoff();
  }

  private reconnectNow(): void {
    if (this.stopped) return;
    this.opts.onStatus(this.recoveringStatus());
    void this.connect();
  }

  private scheduleBackoff(): void {
    if (this.stopped) return;
    const index = Math.min(this.failures, BACKOFF_MS.length - 1);
    this.failures += 1;
    if (this.failures > BACKOFF_MS.length) {   // the 7th consecutive failure
      this.opts.onStatus('offline');
      this.retryWhenReachable();
      return;
    }
    this.opts.onStatus(this.recoveringStatus());
    const delay = Math.round(BACKOFF_MS[index]! * (0.8 + 0.4 * this.random()));
    this.later(delay, () => void this.connect());
  }

  // First of online / visible / 30 s timer wins and cancels the other two.
  private retryWhenReachable(): void {
    if (this.stopped) return;
    let fired = false;
    const cancel = () => { clearTimeout(timer); this.timers.delete(timer); offVisible(); offOnline(); this.waitingForPage.delete(cancel); };
    const retry = () => { if (fired || this.stopped) return; fired = true; cancel(); this.reconnectNow(); };
    const offVisible = this.page.onVisible(retry);
    const offOnline = this.page.onOnline(retry);
    const timer = setTimeout(() => { if (!this.stopped) retry(); }, BACKOFF_MS[BACKOFF_MS.length - 1]);
    this.timers.add(timer);
    this.waitingForPage.add(cancel);
  }

  private whenVisible(fn: () => void): void {
    if (this.stopped) return;
    if (this.page.isVisible()) { fn(); return; }
    let fired = false;
    const off = this.page.onVisible(() => {
      if (fired || this.stopped || !this.page.isVisible()) return;
      fired = true; off(); this.waitingForPage.delete(off); fn();
    });
    this.waitingForPage.add(off);
  }

  private startPings(): void {
    this.stopPings();
    this.lastPingSentAt = 0;
    this.pingTimer = setInterval(() => {
      if (this.stopped || !this.isOpen || !this.pingSupported) return;
      const now = Date.now();
      const pingedSinceInbound = this.lastPingSentAt > this.lastInbound;
      if (this.page.isVisible() && pingedSinceInbound && now - this.lastInbound >= SILENCE_LIMIT_MS) {
        this.declareDead();
        return;
      }
      if (this.send({ type: 'ping' })) this.lastPingSentAt = now;
    }, PING_INTERVAL_MS);
  }

  private declareDead(): void {
    const s = this.socket;
    if (!s) return;
    detach(s);
    try { s.close(4001, 'silent'); } catch { /* already dead */ }
    this.handleClose(4001, 'silent');   // do not wait for an onclose that may never come
  }

  private stopPings(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private scheduleRotation(): void {
    const delay = Math.max(5_000, this.expiresAt - Date.now() - ROTATE_LEAD_MS);
    this.later(delay, () => this.whenVisible(() => void this.rotate()));
  }

  // Mint first, open the new socket, close the old one only once the new one is open.
  private async rotate(): Promise<void> {
    if (this.stopped || this.rotating || !this.isOpen || this.next) return;
    this.rotating = true;
    const epoch = this.epoch;
    let minted: MintedToken;
    try {
      minted = await this.opts.mint();
    } catch (e) {
      if (this.stopped || epoch !== this.epoch) return;
      this.rotating = false;
      const err = e instanceof ChatRequestError ? e.error : toChatError('network');
      if (TERMINAL_MINT_CODES.has(err.code)) { this.terminate(err.code); return; }
      const wait = err.retryAfterSeconds ? err.retryAfterSeconds * 1000 : ROTATE_RETRY_MS;
      this.later(wait, () => this.whenVisible(() => void this.rotate()));   // current socket stays open
      return;
    }
    if (this.stopped || epoch !== this.epoch) return;   // socket closed meanwhile; its own reconnect mints
    this.rotating = false;
    try { this.openSocket(minted, true); } catch {
      this.later(ROTATE_RETRY_MS, () => this.whenVisible(() => void this.rotate()));
    }
  }

  private terminate(reason: string): void {
    this.stop();
    this.opts.onClosed(reason);
  }

  private later(ms: number, fn: () => void): void {
    if (this.stopped) return;
    const t = setTimeout(() => { this.timers.delete(t); if (!this.stopped) fn(); }, ms);
    this.timers.add(t);
  }

  private clearPageListeners(): void {
    this.waitingForPage.forEach((off) => off());
    this.waitingForPage.clear();
  }

  private clearTimers(): void {
    this.timers.forEach(clearTimeout);
    this.timers.clear();
  }
}
